import React from 'react';
import { Box, Typography, Autocomplete, TextField } from '@mui/material';
import { colors, fonts } from '../../../../constants';
import { REGION_OPTIONS } from '../../../../constants/raiseQueryForm';
import FormFieldLabel from '../FormFieldLabel';
import { getInputStyle, getSelectStyle, getHelperTextStyles } from '../../../../utils/formUtils';

/**
 * RegionField Component
 * Region selection field
 */
const RegionField = ({
  formData,
  errors,
  focusedFields,
  isRegionSelectionEnabled,
  regionOptions = REGION_OPTIONS,
  handleRegionChange,
  handleFocus,
  handleBlur,
}) => {
  const helperTextBaseStyles = getHelperTextStyles();

  const getInputStyleWithFocus = (field, hasError) => {
    const isFocused = focusedFields[field];
    return getInputStyle(field, hasError, isFocused);
  };

  const getSelectStyleWithFocus = (field, hasError) => {
    const isFocused = focusedFields[field];
    return getSelectStyle(field, hasError, isFocused);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <FormFieldLabel label="Region" required={isRegionSelectionEnabled} />
      {!isRegionSelectionEnabled ? (
        <input
          type="text"
          placeholder="Not applicable"
          value={regionOptions.find((opt) => opt.value === formData.region)?.label || formData.region || ''}
          disabled={true}
          onFocus={handleFocus('region')}
          onBlur={handleBlur('region')}
          style={{
            ...getInputStyleWithFocus('region', !!errors.region),
            width: '100%',
            boxSizing: 'border-box',
            cursor: 'not-allowed',
            opacity: 1,
            backgroundColor: colors.backgroundSoft,
            border: `1.5px solid ${colors.borderHover}`,
            color: colors.textPrimaryDark,
            padding: '12px 14px',
            height: '42px',
            lineHeight: '1.4',
          }}
        />
      ) : (
        <Autocomplete
          options={regionOptions}
          getOptionLabel={(option) => option.label || ''}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={regionOptions.find((opt) => opt.value === formData.region) || null}
          onChange={(event, newValue) => {
            const value = newValue ? newValue.value : '';
            handleRegionChange({ target: { value } });
          }}
          onFocus={handleFocus('region')}
          onBlur={handleBlur('region')}
          openOnFocus
          filterOptions={(options, params) => {
            const filtered = options.filter((option) =>
              option.label.toLowerCase().includes(params.inputValue.toLowerCase())
            );
            return filtered;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                readOnly: false,
                autoComplete: 'off',
              }}
              placeholder="Type to search region..."
              error={!!errors.region}
              sx={{
                '& .MuiOutlinedInput-root': {
                  ...getSelectStyleWithFocus('region', !!errors.region),
                  padding: '4px 9px',
                  backgroundColor: colors.backgroundWhite,
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '8px 4px',
                  fontSize: fonts.fontSizeMd,
                  cursor: 'text',
                  color: '#1b2538',
                },
              }}
            />
          )}
          sx={{
            width: '100%',
            '& .MuiAutocomplete-inputRoot': {
              padding: '0 !important',
            },
            '& .MuiSvgIcon-root': {
              color: colors.primaryDark,
            },
            '& .MuiAutocomplete-tag': {
              backgroundColor: colors.primaryTint,
              color: colors.primaryDark,
            },
            '& .MuiAutocomplete-option': {
              '&.Mui-focused': {
                backgroundColor: colors.primaryTint,
              },
              '&[aria-selected="true"]': {
                backgroundColor: colors.primaryTintHover,
                '&.Mui-focused': {
                  backgroundColor: colors.primaryTintHover,
                },
              },
            },
          }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          ...helperTextBaseStyles,
          color: errors.region ? colors.error : colors.primaryDark,
        }}
      >
        {errors.region}
      </Typography>
    </Box>
  );
};

export default RegionField;
